import { GameEvent } from './game-event';
import { HotkeyOption } from './hotkey-option';
import { isFloorball, isHockeyVariant, Sport } from './sport';

export { optionForHotkey } from './hotkey-option';

// How play resumes after the clock stopped. The restart restarts the clock.
// Floorball resumes with a face-off (its own event, it has a winner), a hit-in,
// a free hit or a penalty shot (IFF Rules 2026, "fixed situations"); the
// interruption before it already says why play stopped, so it has no reason.
export type GameRestartType =
  | 'centre_pass'
  | 'free_hit'
  | 'hit_in'
  | 'penalty_shot'
  | 'penalty_stroke'
  | 'penalty_shootout'
  | 'long_corner'
  | 'penalty_corner';

export type GameRestartReason =
  | 'ball_out'
  | 'foul'
  | 'after_goal'
  | 'after_card'
  | 'period_start'
  | 'timeout'
  | 'other';

// The hotkey is the letter the collector presses once the restart (r) is chosen.
export type GameRestartOption<T> = HotkeyOption<T>;

interface SportGameRestartOption<T> extends GameRestartOption<T> {
  collectedBy: (sport: Sport) => boolean;
}

const isFieldHockey = (sport: Sport) => sport === 'FIELD_HOCKEY';

const isHockeyVariantOrFloorball = (sport: Sport) =>
  isHockeyVariant(sport) || isFloorball(sport);

const GAME_RESTART_TYPES: SportGameRestartOption<GameRestartType>[] = [
  {
    value: 'centre_pass',
    label: 'Centre pass',
    hotkey: 'c',
    collectedBy: isHockeyVariant
  },
  {
    value: 'hit_in',
    label: 'Hit-in',
    hotkey: 'i',
    collectedBy: isFloorball
  },
  {
    value: 'free_hit',
    label: 'Free hit',
    hotkey: 'f',
    collectedBy: isHockeyVariantOrFloorball
  },
  {
    value: 'penalty_shot',
    label: 'Penalty shot',
    hotkey: 's',
    collectedBy: isFloorball
  },
  {
    value: 'penalty_stroke',
    label: 'Penalty stroke',
    hotkey: 's',
    collectedBy: isHockeyVariant
  },
  {
    value: 'penalty_shootout',
    label: 'Penalty shootout',
    hotkey: 'o',
    collectedBy: isHockeyVariant
  },
  {
    value: 'long_corner',
    label: 'Long corner',
    hotkey: 'l',
    collectedBy: isFieldHockey
  },
  {
    value: 'penalty_corner',
    label: 'Penalty corner',
    hotkey: 'p',
    collectedBy: isHockeyVariant
  }
];

const GAME_RESTART_REASONS: SportGameRestartOption<GameRestartReason>[] = [
  {
    value: 'ball_out',
    label: 'Ball out',
    hotkey: 'b',
    collectedBy: isHockeyVariant
  },
  { value: 'foul', label: 'Foul', hotkey: 'f', collectedBy: isHockeyVariant },
  {
    value: 'after_goal',
    label: 'After goal',
    hotkey: 'g',
    collectedBy: isHockeyVariant
  },
  {
    value: 'after_card',
    label: 'After card',
    hotkey: 'c',
    collectedBy: isHockeyVariant
  },
  {
    value: 'period_start',
    label: 'Period start',
    hotkey: 'p',
    collectedBy: isHockeyVariant
  },
  {
    value: 'timeout',
    label: 'Timeout',
    hotkey: 't',
    collectedBy: isHockeyVariant
  },
  { value: 'other', label: 'Other', hotkey: 'o', collectedBy: isHockeyVariant }
];

export function gameRestartTypesFor(
  sport: Sport
): GameRestartOption<GameRestartType>[] {
  return GAME_RESTART_TYPES.filter((option) => option.collectedBy(sport));
}

export function gameRestartReasonsFor(
  sport: Sport
): GameRestartOption<GameRestartReason>[] {
  return GAME_RESTART_REASONS.filter((option) => option.collectedBy(sport));
}

/**
 * A face-off collected where floorball resumes play with another restart.
 * Keeps when, where and who, drops what only a face-off has (its winner, the
 * opponent and the paired event of the other team).
 */
export function faceOffAsRestart(
  faceOff: GameEvent,
  gameRestartType: GameRestartType
): GameEvent {
  const {
    _id,
    faceoffId,
    teamFaceOffOutcome,
    faceoff_opponent,
    faceoff_opponentId,
    faceoffOutcomeXPosition,
    faceoffOutcomeYPosition,
    validations,
    insertDate,
    insertUser,
    modificationDate,
    modificationUser,
    ...restart
  } = faceOff;
  return { ...restart, eventType: 'gameRestart', gameRestartType };
}
